import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import { TextField } from "@mui/material";
import { CircularProgress } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../Context/CartContext"; // added: use cart provider
import { resolveImageUrl, getImageUrl } from "../services/indexMediaBaseUrl"; // robust resolver


const STORAGE_KEY = "cart";

export default function CartDrawer() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("cart");
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  // cart from context (preferred)
  const cartCtx = useCart();
  // cartCtx.cart may be shape: { success:true, data: { items: [...] } } or { data: { items: [...] } }
  const ctxItemsRaw =
    cartCtx?.cart?.data?.items || cartCtx?.cart?.items || cartCtx?.cart || null;
  const [localFallbackItems, setLocalFallbackItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [updatingIds, setUpdatingIds] = useState([]); // track updating items

  // normalize items for display: prefer context items, else local fallback
  const sourceItems = Array.isArray(ctxItemsRaw)
    ? ctxItemsRaw
    : localFallbackItems;

  const items = sourceItems.map((it) => {
    // if item comes from API shape include product nested object
    const product = it.product || {};
    // resolve size/color objects/labels
    const sizeObj =
      it.size ||
      (it.size_id && Array.isArray(product.sizes)
        ? product.sizes.find((s) => String(s.id) === String(it.size_id))
        : null);
    const colorObj =
      it.color ||
      (it.color_id && Array.isArray(product.colors)
        ? product.colors.find((c) => String(c.id) === String(it.color_id))
        : null);
    const sizeLabel = sizeObj
      ? sizeObj.size || sizeObj.key || String(sizeObj.id)
      : "";
    const colorLabel = colorObj
      ? colorObj.color_en || colorObj.color_ar || String(colorObj.id)
      : "";

    // potential photo sources (robust search)
    const photoCandidates = [
      product?.main_photo,
      product?.images?.[0]?.image,
      it.main_photo,
      it.image,
      it.photo,
      it.product_main_photo,
      it.product?.main_photo,
      it.product?.images?.[0]?.image,
    ];

    const rawPhoto = photoCandidates.find((p) => p && String(p).trim()) || "";

    // resolve to final URL using helper
    const imageUrl = rawPhoto ? resolveImageUrl(rawPhoto) : "/placeholder.png";

    // debug log to track issues
    // eslint-disable-next-line no-console
    console.debug("CartDrawer:image", {
      item_id: it.id ?? it.item_id ?? it.product_id,
      rawPhoto,
      imageUrl,
      productPresent: !!it.product,
    });

    return {
      item_id: it.id, // ✅ cart item id (ده اللي الـ API عايزه)
      product_id: it.product_id, // ✅ للعرض فقط
      qty: it.quantity ?? it.qty ?? 1,
      price:
        (it.price && Number(it.price)) ||
        Number(product?.currencies?.[0]?.price) ||
        Number(it.unit_price) ||
        0,
      name:
        product?.name_en ||
        product?.name_ar ||
        it.name ||
        it.title ||
        `Product ${it.product_id || it.item_id}`,
      image: imageUrl,
      // pass full size/color data so MakeOrderPage can use IDs/labels/objects
      size_id: it.size_id ?? sizeObj?.id ?? null,
      color_id: it.color_id ?? colorObj?.id ?? null,
      size: sizeObj || null,
      color: colorObj || null,
      sizeLabel,
      colorLabel,
      raw: it,
    };
  });

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        try {
          setLocalFallbackItems(JSON.parse(e.newValue || "[]"));
        } catch {
          setLocalFallbackItems([]);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const refresh = () => {
    // prefer context refresh if provided
    if (typeof cartCtx?.refresh === "function") {
      cartCtx.refresh();
    } else if (typeof cartCtx?.refreshCart === "function") {
      cartCtx.refreshCart();
    } else {
      try {
        setLocalFallbackItems(
          JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"),
        );
      } catch {
        setLocalFallbackItems([]);
      }
    }
  };

  const count = items.reduce((s, it) => s + (Number(it.qty) || 1), 0);
  const total = items.reduce(
    (s, it) => s + Number(it.price || 0) * (Number(it.qty) || 1),
    0,
  );

  const openDrawer = () => {
    refresh();
    setOpen(true);
  };
  const closeDrawer = () => setOpen(false);

  const removeItem = async (itemId) => {
    if (typeof cartCtx?.removeItem === "function") {
      await cartCtx.removeItem({ item_id: itemId }); // ✅
      await cartCtx.refreshCart?.();
      return;
    }

    // fallback to localStorage approach: remove items that match product_id or nested product.id
    try {
      const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      const next = current.filter((it) => {
        const pid = it.product_id ?? it.product?.id ?? it.product_id;
        return String(pid) !== String(itemId);
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setLocalFallbackItems(next);
    } catch {
      setLocalFallbackItems([]);
    }
  };

  const clearCart = async () => {
    if (typeof cartCtx?.clearCart === "function") {
      setClearing(true);
      try {
        await cartCtx.clearCart();
        await cartCtx.refreshCart?.(); // ✅ هتجيب السلة فاضية
      } catch (err) {
        console.error("clearCart error:", err?.response?.data || err);
      } finally {
        setClearing(false);
      }
      return;
    }

    // fallback local
    setLocalFallbackItems([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  const goToCart = () => {
    closeDrawer();
    // انتقل إلى صفحة الدفع ومرّر بيانات الأصناف ضمن state
    // IMPORTANT: items here already include .image (resolved)
    navigate("/make-order", { state: { items } });
  };

  const handleUpdateItem = async (itemId, quantity) => {
    if (!itemId) return;
    const qty = Math.max(1, Number(quantity || 1));
    // prefer context update
    if (typeof cartCtx?.updateCartItem === "function") {
      setUpdatingIds((s) => [...s, itemId]);
      try {
        await cartCtx.updateCartItem({ item_id: itemId, quantity: qty });
        await cartCtx.refreshCart?.();
      } catch (err) {
        console.error("updateCartItem error:", err);
      } finally {
        setUpdatingIds((s) => s.filter((id) => id !== itemId));
      }
      return;
    }

    // fallback to localStorage: find matching cart item by id/item_id/product_id and update quantity
    try {
      const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      const next = current.map((it) => {
        const iid = it.id ?? it.item_id ?? it.itemId;
        const pid = it.product_id ?? it.product?.id;
        if (String(iid) === String(itemId) || String(pid) === String(itemId)) {
          return { ...it, quantity: qty };
        }
        return it;
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setLocalFallbackItems(next);
    } catch (err) {
      console.error("local update error:", err);
    }
  };

  return (
    <>
      <IconButton
        onClick={openDrawer}
        aria-label="cart"
        sx={{ color: "primary.main" }}
      >
        <Badge badgeContent={count} color="error">
          <ShoppingCartIcon />
        </Badge>
      </IconButton>

      <Drawer anchor="right" open={open} onClose={closeDrawer}>
        <Box sx={{ width: 360, p: 2 }}>
          <Typography variant="h6">{t("cart", "Cart")}</Typography>
          <Divider sx={{ my: 1 }} />
          {items.length === 0 ? (
            <Typography variant="body2" sx={{ mt: 2 }}>
              {t("cart_empty", "السلة فارغة")}
            </Typography>
          ) : (
            <List>
              {items.map((it) => (
                <ListItem
                  key={it.item_id}
                  secondaryAction={
                    <Button size="small" onClick={() => removeItem(it.item_id)}>
                      {t("remove", "حذف")}
                    </Button>
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      src={it.image || "/placeholder.png"}
                      alt={it.name || ""}
                      onError={(e) => {
                        // fallback to placeholder if image fails to load
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/placeholder.png";
                      }}
                      variant="rounded"
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      it.name ||
                      it.title ||
                      `Product ${it.product_id || it.item_id}`
                    }
                    secondary={
                      <>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <TextField
                            type="number"
                            size="small"
                            defaultValue={it.qty || 1}
                            inputProps={{ min: 1 }}
                            sx={{ width: 96 }}
                            disabled={updatingIds.includes(it.item_id)}
                            onBlur={(e) => {
                              const v = Math.max(
                                1,
                                Number(e.target.value || 1),
                              );
                              if (v !== (it.qty || 1))
                                handleUpdateItem(
                                  it.item_id ?? it.id ?? it.product_id,
                                  v,
                                );
                            }}
                          />
                          {updatingIds.includes(it.item_id) ? (
                            <CircularProgress size={18} />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {`${it.qty || 1} × ${it.price || ""}`}
                            </Typography>
                          )}
                        </Box>
                        {(it.sizeLabel || it.colorLabel) && (
                          <Typography
                            component="div"
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {it.sizeLabel ? `المقاس: ${it.sizeLabel}` : ""}
                            {it.sizeLabel && it.colorLabel ? " • " : ""}
                            {it.colorLabel ? `اللون: ${it.colorLabel}` : ""}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 1 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <Typography variant="subtitle1">{t("total", "المجموع")}</Typography>
            <Typography variant="subtitle1">{total.toFixed(2)}</Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={goToCart}
              disabled={items.length === 0}
            >
              {t("go_to_cart", "اذهب للسلة")}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearCart}
              disabled={clearing}
            >
              {t("clear_cart", "تفريغ")}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
